import { z } from "zod";

/* ===========================================================================
 * US-E7.F1-0079 Activity Feed & Action Items (@UC0074) — Engagement (Phase 8.5, read-only).
 *
 *  - GET /v1/groups/:id/activity — feed hoạt động nhóm (projection READ-ONLY từ bảng nguồn sự thật:
 *    Collection/Expense/Consensus/GroupMember), cursor keyset (createdAt,id desc), scope membership.
 *    Redaction fail-closed: Member thường + fund transparency=private → OMIT `amountMinor` (ẩn số tiền).
 *  - GET /v1/me/action-items — badge việc cần làm: đếm dues unpaid/overdue + vote đang mở chưa bỏ +
 *    pay-on-behalf chờ tôi respond. Chỉ của tôi (IDOR — userId từ token).
 *
 * Tiền STRING minor unit (B1, no float). KHÔNG post/update ledger — chỉ SELECT.
 * ========================================================================= */

const MONEY_INTEGER_STRING_PATTERN = /^-?\d+$/;

/**
 * Loại sự kiện hiển thị trong feed (projection từ bảng nguồn — semantics khớp outbox eventType).
 *
 * 🔴 US-E24.F12-0361 (@UC9591): `PAYMENT_CONFIRMED` = **tiền VÀO ĐÃ XÁC NHẬN** (trạng thái 3 của
 * CLAUDE.md §3.7), projection từ `ledger_transactions` — bảng FACT append-only. Tên cố ý TRÙNG
 * eventType outbox đang có (`apps/worker/.../event-types.ts`), KHÔNG đặt tên mới.
 * ⚠️ Lời khai chưa duyệt (`member_dues.status='awaiting_confirmation'`) KHÔNG BAO GIỜ là sự kiện
 * này — đưa claim lên feed công khai của cả nhóm là sổ ảo + bêu tên.
 *
 * 🔴 Whitelist này khai HAI LẦN (object hằng + `z.enum` dưới). Thêm giá trị phải sửa CẢ HAI —
 * guard tự-khám-phá `activity-event-type.test.ts` so `schema.options` với `Object.values(...)`.
 */
export const ACTIVITY_EVENT_TYPE = {
  COLLECTION_CREATED: "collection.created",
  EXPENSE_APPROVED: "expense.approved",
  EXPENSE_REJECTED: "expense.rejected",
  MEMBER_JOINED: "member.joined",
  /**
   * 🔴 US-E24.F50-0502 (@UC0074): NGƯỜI RỜI NHÓM. Feed cũ chỉ có `member.joined` ⇒ sĩ số trên feed
   * chỉ tăng, không bao giờ giảm, và nhóm không đối chiếu được vì sao kỳ này ít người đóng hơn kỳ
   * trước. Projection từ **`group_members.leftAt`** — cột đã có sẵn, khai đúng nghĩa "MỐC RỜI"
   * (US-E19.F3-0202). KHÔNG suy từ `status`: `join_pending` vừa là "chờ được duyệt vào" vừa là
   * "đã rời, nằm hàng đợi" (QĐ PO 13/08, US-E4.F2-0396) ⇒ suy từ đó sẽ dựng dòng *"X đã rời nhóm"*
   * cho người mới xin vào. 0 migration, 0 bảng mới.
   */
  MEMBER_LEFT: "member.left",
  PAYMENT_CONFIRMED: "payment.confirmed",
} as const;

export type ActivityEventType =
  (typeof ACTIVITY_EVENT_TYPE)[keyof typeof ACTIVITY_EVENT_TYPE];

export const activityEventTypeSchema = z.enum([
  ACTIVITY_EVENT_TYPE.COLLECTION_CREATED,
  ACTIVITY_EVENT_TYPE.EXPENSE_APPROVED,
  ACTIVITY_EVENT_TYPE.EXPENSE_REJECTED,
  ACTIVITY_EVENT_TYPE.MEMBER_JOINED,
  ACTIVITY_EVENT_TYPE.MEMBER_LEFT,
  ACTIVITY_EVENT_TYPE.PAYMENT_CONFIRMED,
]);

/**
 * Giá trị filter `?kind` của feed (US-E24.F12-0361 · @UC9593) — màn Figma B3 có chip **Thu / Chi**.
 *
 * 🔴 CỐ Ý KHÔNG có `all`: "Tất cả" = **không gửi param**. Một khái niệm, một cách biểu diễn
 * (mirror lý do repo cố ý không có enum `biweekly`) — thêm `all` là hai đường cho cùng một ý, rồi
 * sẽ có chỗ xử `all` còn chỗ xử `undefined`.
 */
export const ACTIVITY_KIND = {
  INCOME: "income",
  EXPENSE: "expense",
} as const;

export type ActivityKind = (typeof ACTIVITY_KIND)[keyof typeof ACTIVITY_KIND];

export const activityKindSchema = z.enum([ACTIVITY_KIND.INCOME, ACTIVITY_KIND.EXPENSE]);

/**
 * 🔴 PHÂN LOẠI THU / CHI / TRUNG TÍNH của từng eventType — khai **MỘT CHỖ**, server lọc và client
 * hiện chip đều đọc chung. `Record<ActivityEventType, …>` ⇒ thêm eventType mà quên phân loại là
 * **lỗi typecheck**, và guard runtime `activity-event-kind.test.ts` bắt thêm lần nữa.
 *
 * Vì sao có `neutral` chứ không nhét mọi thứ vào thu/chi:
 *  - `collection.created` = **NGHĨA VỤ** (nhóm quyết phải đóng) — chưa đồng nào vào quỹ. Xếp vào
 *    "thu" là đếm tiền chưa tồn tại (đúng thứ CLAUDE.md §3.7 tách bạch: nghĩa vụ ≠ đã nhận).
 *  - `expense.rejected` = **KHÔNG có tiền ra**. Xếp vào "chi" là hiện khoản chi không hề xảy ra.
 *  - `member.joined` / `member.left` = không dính tiền. Người rời nhóm mà còn nghĩa vụ đã bị chặn ở
 *    tầng khác (`hasNonZeroPosition`), nên dòng này KHÔNG BAO GIỜ là một khoản thu/chi.
 *
 * 🔴 Phạm vi là **BÁO CÁO NHÓM**, không phải "xem một sổ" (`packages/domain/src/ledger.ts` khai
 * hai tập cố ý khác nhau đúng phần tử `FUND_TRANSFER_IN`). Nếu sau này feed có thêm sự kiện
 * chuyển tiền liên sổ, nó **KHÔNG** thuộc `income` — tiền chỉ dời chỗ trong cùng nhóm.
 */
export const ACTIVITY_EVENT_KIND: Record<ActivityEventType, ActivityKind | "neutral"> = {
  [ACTIVITY_EVENT_TYPE.COLLECTION_CREATED]: "neutral",
  [ACTIVITY_EVENT_TYPE.EXPENSE_APPROVED]: ACTIVITY_KIND.EXPENSE,
  [ACTIVITY_EVENT_TYPE.EXPENSE_REJECTED]: "neutral",
  [ACTIVITY_EVENT_TYPE.MEMBER_JOINED]: "neutral",
  [ACTIVITY_EVENT_TYPE.MEMBER_LEFT]: "neutral",
  [ACTIVITY_EVENT_TYPE.PAYMENT_CONFIRMED]: ACTIVITY_KIND.INCOME,
};

/**
 * 1 mục trong feed. `amountMinor` OMIT khi redacted (Member + fund private) HOẶC sự kiện không có tiền
 * (member.joined / member.left). `title` = mô tả ngắn (tên đợt thu / tên chi / tên thành viên).
 * `fundId` null với member.joined / member.left (không thuộc fund cụ thể).
 *
 * 🔴🔴 US-E24.F50-0502 (@UC0074) — `title` KHÔNG PHẢI ACTOR, đừng để client suy ra.
 * `title` đa hình theo nguồn (tên đợt thu · tên khoản chi · tên người nộp · tên người vào/rời nhóm),
 * nên từ nó không đọc ra được "ai làm việc này". `actorName`/`actorAvatarUrl` là field RIÊNG cho
 * NGƯỜI THỰC HIỆN, lấy từ **chủ thể nghiệp vụ** của từng loại sự kiện:
 *
 *  | eventType            | actor = ai                          | nguồn cột                                  |
 *  |----------------------|-------------------------------------|--------------------------------------------|
 *  | collection.created   | người MỞ đợt thu                    | `collections.createdBy` (worker ⇒ NULL)    |
 *  | expense.approved/rej | người LẬP khoản chi                 | `expenses.createdBy`                        |
 *  | payment.confirmed    | **người NỘP** (KHÔNG phải thủ quỹ)  | `member_dues → group_members` / `pay_on_behalf.payerMemberId` |
 *  | member.joined/left   | chính thành viên đó                 | `group_members`                             |
 *
 * 🔴 `payment.confirmed` là ca zero-tolerance: `/record-paid` chỉ O/A/T gọi được nên
 * `ledger_transactions.createdBy` là **THỦ QUỸ**. Lấy actor từ đó ⇒ feed cả nhóm đọc thấy *"Thủ quỹ
 * đã đóng quỹ 200.000đ"* cho khoản của người khác — đúng lớp bug đắt nhất repo
 * (CLAUDE.md §3.6 `expenses.payerMemberId`: *"suy chủ thể từ actor đang bấm nút là SAI khi hành
 * động cần quyền cao hơn người trong cuộc"*).
 *
 * 🔴 `actorName = null` có ĐÚNG HAI nghĩa và client phân biệt bằng ngữ cảnh, KHÔNG bằng chuỗi rỗng:
 * (a) việc do HỆ THỐNG chạy (worker sinh đợt thu ⇒ `createdBy` NULL) — client hiện "Hệ thống";
 * (b) danh tính bị ẩn theo `payment_status_visibility` / quỹ private. `''` là "có tên mà rỗng" ⇒
 * cấm (mirror lý do `amountMinor` bị OMIT chứ không hạ về `"0"` — số 0 là nói dối về tiền).
 */
export const activityItemSchema = z
  .object({
    id: z.string().uuid(),
    eventType: activityEventTypeSchema,
    fundId: z.string().uuid().nullable(),
    title: z.string().nullable(),
    /** Tên NGƯỜI THỰC HIỆN. null = hệ thống chạy HOẶC danh tính bị ẩn (xem docblock trên). */
    actorName: z.string().nullable(),
    /** Ảnh đại diện người thực hiện — **URL đã resolve**, không phải object key. */
    actorAvatarUrl: z.string().nullable(),
    /**
     * "Chi cho việc gì" — `expenses.description`. null với mọi eventType khác, và **null khi khoản
     * chi không `public`**: `schema.prisma` khai `Expense.visibility` chi phối CẢ số tiền LẪN
     * `description` ("ghi chú nhạy cảm — repo không có bảng note riêng"), mà feed là bề mặt CÔNG
     * KHAI của cả nhóm. Vắng mô tả ⇒ null, KHÔNG chuỗi rỗng.
     */
    description: z.string().nullable(),
    /** Số tiền của sự kiện (STRING minor unit, B1) — OMIT khi redacted / sự kiện không có tiền. */
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN).optional(),
    currency: z.string().length(3).nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();

export type ActivityItem = z.infer<typeof activityItemSchema>;

/** Response feed hoạt động nhóm — cursor keyset. `redacted`=true khi Member bị ẩn số tiền nào đó. */
export const activityFeedResponseSchema = z
  .object({
    data: z.array(activityItemSchema),
    pageInfo: z.object({
      hasNext: z.boolean(),
      nextCursor: z.string().nullable(),
    }),
    redacted: z.boolean(),
  })
  .strict();

export type ActivityFeedResponse = z.infer<typeof activityFeedResponseSchema>;

/**
 * Response action-items (badge + card enrich). `totalCount` = tổng số việc cần làm. Mỗi item mang
 * ĐỦ dữ liệu render card mà KHÔNG cần call thứ 2 (fundName/collectionTitle/amount/dueDate/deadline).
 * Không việc → totalCount=0 + mảng rỗng + history tổng "0".
 *
 * Enrich (S25 · @UC0074): field mở rộng chỉ từ COLUMN thật (schema.prisma) — KHÔNG bịa:
 *  - dues/payOnBehalf: fundName (Fund.name), collectionTitle (Collection.title|null),
 *    amountMinor (MemberDue.amountMinor BigInt→string), currency, dueDate (Collection.dueDate|null),
 *    daysLeft (derive server ceil((dueDate-now)/86400000)|null khi dueDate null).
 *  - openVotes: expenseTitle (Expense.name|null), amountMinor (Expense.amountMinor→string), currency,
 *    deadline (Consensus.deadline|null — null hợp lệ khi mechanism=admin_only, KHÔNG gán lên Expense).
 *  - history: reuse tổng paid/owed/waived (BigInt→string) + recent dues đã đóng/miễn.
 */
export const actionItemsResponseSchema = z
  .object({
    totalCount: z.number().int().nonnegative(),
    /** Dues của tôi chưa đóng (pending/overdue) — enrich đủ để render card nộp phí. */
    dues: z.array(
      z
        .object({
          memberDueId: z.string().uuid(),
          collectionId: z.string().uuid(),
          status: z.enum(["pending", "overdue"]),
          fundName: z.string(),
          collectionTitle: z.string().nullable(),
          amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
          currency: z.string().length(3),
          dueDate: z.string().datetime().nullable(),
          daysLeft: z.number().int().nullable(),
        })
        .strict(),
    ),
    /** Phiên vote đang mở tôi CHƯA bỏ phiếu — enrich tiêu đề/số tiền/hạn để render card vote. */
    openVotes: z.array(
      z
        .object({
          expenseId: z.string().uuid(),
          consensusId: z.string().uuid(),
          expenseTitle: z.string().nullable(),
          amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
          currency: z.string().length(3),
          deadline: z.string().datetime().nullable(),
        })
        .strict(),
    ),
    /** Đề nghị nộp-hộ chờ tôi (beneficiary) respond — mirror dues enrich (fund/tên đợt/số tiền). */
    payOnBehalf: z.array(
      z
        .object({
          payOnBehalfId: z.string().uuid(),
          memberDueId: z.string().uuid(),
          fundName: z.string(),
          collectionTitle: z.string().nullable(),
          amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
        })
        .strict(),
    ),
    /** Lịch sử đóng của tôi (paid/paid_on_behalf/waived) — tổng string + recent rows. */
    history: z
      .object({
        totalPaidMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
        totalOwedMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
        totalWaivedMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
        recent: z.array(
          z
            .object({
              memberDueId: z.string().uuid(),
              collectionTitle: z.string().nullable(),
              amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
              status: z.string(),
              dueDate: z.string().datetime().nullable(),
            })
            .strict(),
        ),
      })
      .strict(),
  })
  .strict();

export type ActionItemsResponse = z.infer<typeof actionItemsResponseSchema>;
